// Words of a record by the value of its field (R-E9, TypeDef.wording): which fields its card hides, how it names them, and how the
// menu names sections while it is the chosen scope. One mechanism for the server and the browser; the data stays the same.

import type { FieldDef, Id, RecordObj } from './model.ts';
import type { MetaIndex } from './meta-index.ts';
import type { QueryContext } from './filter.ts';

export interface RecordWords {
  hidden: Set<string>;
  names: Map<string, string>;
  types: Map<Id, { name: string; nameMany: string }>;
  screens: Set<string>;
}

const EMPTY: RecordWords = { hidden: new Set(), names: new Map(), types: new Map(), screens: new Set() };

/** Reads the record linked through a relation field: the client's store or the server's query context. */
export type WordsLinks = Pick<QueryContext, 'record' | 'linked'>;

/**
 * The rules of the record's type that hold for it, merged in their order: a later rule renames over an earlier one. A rule `via` a
 * relation needs `links` and a linked record; without them it does not hold.
 */
export function recordWords(meta: MetaIndex, rec: Pick<RecordObj, 'typeId' | 'data'> | null | undefined, links?: WordsLinks): RecordWords {
  const rules = rec ? meta.type(rec.typeId)?.wording : null;
  if (!rec || !rules?.length) return EMPTY;
  const out: RecordWords = { hidden: new Set(), names: new Map(), types: new Map(), screens: new Set() };
  for (const r of rules) {
    let from: Pick<RecordObj, 'data'> | undefined = rec;
    if (r.via) {
      const f = meta.field(rec.typeId, r.via);
      const id = f && links && 'id' in rec ? links.linked(rec as RecordObj, f)[0] : undefined;
      from = id ? links!.record(id) : undefined;
      if (!from) continue;
    }
    const raw = from.data[r.field];
    // A record from before the field existed has no value: «not X» is unknown there, so it hides nothing (audit wave C, P3-1).
    if (raw === undefined && r.not) continue;
    const values = (Array.isArray(raw) ? raw : raw == null || raw === '' ? [] : [raw]).filter((v): v is string => typeof v === 'string');
    const named = new Set<Id>();
    for (const t of r.terms) for (const x of meta.subtree(t)) named.add(x);
    const has = values.some((v) => named.has(v));
    if (has === !!r.not) continue;
    for (const k of r.hide ?? []) out.hidden.add(k);
    for (const [k, v] of Object.entries(r.rename ?? {})) out.names.set(k, v);
    for (const [k, v] of Object.entries(r.types ?? {})) out.types.set(k, v);
    for (const k of r.screens ?? []) out.screens.add(k);
  }
  return out;
}

/** The fields of a card as its record words them: hidden ones left out, renamed ones under their other name. */
export function wordedFields(fields: FieldDef[], words: RecordWords): FieldDef[] {
  if (!words.hidden.size && !words.names.size) return fields;
  return fields.filter((f) => !words.hidden.has(f.key)).map((f) => (words.names.has(f.key) ? { ...f, name: words.names.get(f.key)! } : f));
}
